
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { MODEL } from "@/module";
import { ref } from "vue";
import { v4 as uuidv4 } from "uuid";
import { BlockBlobClient, AnonymousCredential } from "@azure/storage-blob";
import VueCropper from "vue-cropperjs";
import html2canvas from "html2canvas";
import "cropperjs/dist/cropper.css";
import { NavigationGuardNext, Route } from "vue-router";

@Component({
  components: {
    VueCropper,
  },
})
export default class CardMaker extends Vue {
  @Prop({ required: true }) cardType?: MODEL.CardType;
  private vertical = false;
  private imgSrc = ref();
  private bgImgSrc = ref();
  private overlayImgSrc? = ref();
  private cropedImg?: string | ArrayBuffer | null = "";
  private file = null;
  private memberNo = "";
  private division = "";
  private hideDivision = false;
  private memberName = "";
  private trim = false;
  // private forStaff = false;
  private downloadFileName = "";
  private dataURL = "";
  private dialog = false;
  private uploadDialog = false;
  private canvas = false;
  private snackbar = false;
  private timeout = 2000;
  private uploadURL = "";
  private step = 1;
  private uploading = false;
  private imgBrightness = 100;
  private imgContrast = 100;
  private shareLoading = false;
  private bgId = 0;
  private blob: Blob | null = null;

  private MODE_APPLY_CARD = false;

  created() {
    // const staff = this.$route.query.staff;
    // if (staff != null) {
    //   this.forStaff = true;
    // }
  }

  mounted() {
    this.setUpImgSrc();
  }

  @Watch("step")
  onChangeStep(step: number, stepBefore: number) {
    if (stepBefore >= 3 && step < 3) {
      this.resetImageData();
    }
  }

  @Watch("cardType")
  onChangeCardType() {
    this.bgId = 0;
    this.setUpImgSrc();
    if (this.file != null) {
      this.step = 2;
    } else {
      this.step = 1;
    }
  }

  @Watch("bgId")
  onChangeBgId() {
    this.bgImgSrc = require(`@/assets/${this.cardType?.assets.card_bg[this.bgId]}`);
  }

  private setUpImgSrc() {
    this.bgImgSrc = require(`@/assets/${this.cardType?.assets.card_bg[this.bgId]}`);
    this.imgSrc = require(`@/assets/${this.cardType?.assets.default_photo}`);
    if (this.cardOverlay) {
      this.overlayImgSrc = require(`@/assets/${this.cardOverlay}`);
    } else {
      this.overlayImgSrc = undefined;
    }
  }

  private get cropper() {
    return this.$refs.cropper as any;
  }

  get trimClass(): string {
    return this.trim ? "overflowHidden" : "";
  }

  get profileFilled(): boolean {
    if (this.ema) {
      return this.memberName !== "";
    } else {
      return this.memberNo !== "" && this.memberName !== "" && (this.hideDivision || this.division !== "");
    }
  }

  get filled(): boolean {
    return this.file != null && this.profileFilled;
  }

  get mobile(): boolean {
    return this.$vuetify.breakpoint.mobile;
  }

  get numberLabel(): string {
    return this.$t(`organization.${this.cardType?.organization_type}.membership_no`).toString();
  }

  get logoStyleString(): string {
    return `letter-spacing: ${this.$t("common.logo_spacing", "ja")};`;
  }

  get titleStyleString(): string {
    return `color: ${this.cardType?.colors.title_font};`;
  }

  get memberNameStyle(): string {
    return this.cardType?.layout?.title_pr ? `padding-right: ${this.cardType?.layout?.title_pr}` : "";
  }

  get cardOverlay(): string | undefined {
    return this.cardType?.assets.overlay;
  }

  get zoom(): number {
    if (this.canvas) {
      return 1;
    } else {
      const dispWidth = this.$vuetify.breakpoint.width; // - this.$vuetify.breakpoint.scrollBarWidth;
      const root = this.$refs.root as HTMLElement;
      const [marginTortalm, previewOriginalWidth, maxZoom] = [48, 1920, 0.25];
      return Math.min((dispWidth - marginTortalm) / previewOriginalWidth, maxZoom);
    }
  }

  get canShare(): boolean {
    return navigator.share !== undefined ? true : false;
  }

  private onChangeFileInput() {
    if (this.file == null) {
      this.cropper.destroy();
    } else {
      this.cropper.replace(URL.createObjectURL(this.file));
    }
  }

  private cropImage() {
    // get image data for post processing, e.g. upload or setting image src
    this.cropedImg = this.cropper.getCroppedCanvas().toDataURL();
  }
  private rotate(deg: number) {
    // guess what this does :)
    this.cropper.rotate(deg);
  }

  private move(offsetX: number, offsetY: number) {
    this.cropper.move(offsetX, offsetY);
  }

  private relativeZoom(ratio: number) {
    this.cropper.relativeZoom(ratio);
  }

  private reset(ratio: number) {
    this.cropper.reset();
  }

  private async createCardImage(download: boolean) {
    // api起こす
    if (this.MODE_APPLY_CARD) {
      this.fetchCredential();
    }
    this.canvas = true;
    this.blob = null;
    Vue.nextTick(async () => {
      const frame: HTMLElement = this.$refs.cardFrame as HTMLElement;
      const preview: HTMLElement = this.$refs.cardPreview as HTMLElement;
      const paramsFrame: Parameters<typeof html2canvas> = [frame, { scale: 1 }];
      const paramsPreview: Parameters<typeof html2canvas> = [preview, { scale: 1 }];
      const canvasElement = await html2canvas(...paramsFrame).catch((e) => {
        console.error(e);
        return;
      });
      if (!canvasElement) {
        return;
      }
      const canvasElementForUpload = await html2canvas(...paramsPreview).catch((e) => {
        console.error(e);
        return;
      });
      if (!canvasElementForUpload) {
        return;
      }
      const dataURL = canvasElement.toDataURL("image/png");
      canvasElementForUpload.toBlob((blob) => {
        this.blob = blob;
      });
      this.canvas = false;
      if (download) {
        let link = document.createElement("a");
        link.href = dataURL;
        link.target = "_blank";
        if (this.downloadFileName !== "") {
          link.download = `${this.downloadFileName}.png`;
        } else {
          link.download = `${this.memberNo}_${this.division}_${this.memberName}.png`;
        }
        link.click();
      } else {
        this.dataURL = dataURL;
      }
    });
  }

  private async getCardBlobFromDataURL(dataUrl: string): Promise<Blob | null> {
    return await (await fetch(dataUrl)).blob();
  }

  /**
   * カードのBlobイメージを取得する
   * @param copy
   */
  private async getCardBlob(copy: boolean): Promise<Blob | null> {
    // const preview: HTMLElement = copy ? (this.$refs.cardPreview as HTMLElement) : (this.$refs.cardFrame as HTMLElement);
    // const params: Parameters<typeof html2canvas> = [preview, { scale: 1 }];
    // const canvasElement = await html2canvas(...params).catch((e) => {
    //   console.error(e);
    //   this.uploading = false;
    //   return null;
    // });
    // if (!canvasElement) {
    //   console.error("canvasElement not found");
    //   return null;card_bg
    // }
    // return new Promise((resolve, reject) => {
    //   canvasElement.toBlob((blob) => {
    //     resolve(blob);
    //   });
    // });
    return new Promise((resolve, reject) => {
      resolve(this.blob);
    });
  }

  private async uploadCardImage() {
    this.uploading = true;
    Vue.nextTick(async () => {
      this.getCardBlob(true).then((blob) => {
        if (blob) {
          this.uploadImgToBlobStorage(blob).finally(() => {
            // this.uploading = false;
          });
        } else {
          console.error("blob from canvasElement not found");
          this.uploading = false;
        }
      });
    });
  }

  private generateUUID(): string {
    return uuidv4();
  }

  private fetchCredential(): void {
    fetch("/api/credentials");
  }

  private async uploadImgToBlobStorage(blob: Blob): Promise<void> {
    const cardID = this.generateUUID();
    const filename = `${cardID}.png`;
    fetch("/api/credentials")
      .then((res) => res.json())
      .then((json) => {
        const blockBlobClient = new BlockBlobClient(
          `${json.url}/${this.cardType!.bird_type}/${filename}?${json.sasToken}`,
          new AnonymousCredential()
        );
        return blockBlobClient.uploadData(blob);
      })
      .then((blobRes) => {
        let href = window.location.href;
        if (!href.endsWith("/")) {
          href = href.padEnd(href.length + 1, "/");
        }
        this.uploadURL = `${href}copy/${cardID}`;
        this.uploadDialog = true;
        return;
      })
      .finally(() => {
        this.uploading = false;
      });
    return;
  }

  private resetImageData() {
    console.debug("resetImageData");
    this.dataURL = "";
    this.uploadURL = "";
  }

  private closeDialog() {
    this.dialog = false;
    this.closeUploadDialog();
  }

  private closeUploadDialog() {
    this.uploadDialog = false;
    this.uploadURL = "";
  }

  private cardBGLoaded() {
    this.$nextTick(() => {
      this.trim = true;
    });
  }

  get organizationName(): string {
    return this.$t(`organization.${this.cardType?.organization_type}.name`, {
      msg: this.$t(`bird.${this.cardType?.bird_type}`).toString(),
    }).toString();
  }

  get orgNameHashTag(): string {
    return `#${this.organizationName.replace(/ /g, "")}`;
  }

  get appNameHashTag(): string {
    return `#${"KinkaUnionCardMaker"}`;
  }

  get divisionLabel(): string {
    return this.hideDivision ? this.division : this.$t("caption.division", { msg: this.division }).toString();
  }

  get shareText(): string {
    if (this.ema) {
      return `${this.memberName}

${this.orgNameHashTag}
${this.appNameHashTag}
`;
    } else {
      return `${this.$t("common.welcome_msg", { msg: this.organizationName })}!!

${this.numberLabel} ${this.memberNo}
${this.divisionLabel}
${this.memberName}

${this.orgNameHashTag}
${this.appNameHashTag}
`;
    }
  }

  get shareTextEncoded(): string {
    return encodeURIComponent(this.shareText);
  }

  get tweetShareURL(): string {
    let url = `${location.protocol}//${location.host}${location.pathname}`;
    const shareURL = `https://twitter.com/intent/tweet?text=${this.shareTextEncoded}&url=${url}`;
    return shareURL;
  }

  private async shareCardImg(): Promise<void> {
    if (this.canShare) {
      this.shareLoading = true;
      Vue.nextTick(async () => {
        const blob = await this.getCardBlobFromDataURL(this.dataURL);
        this.shareLoading = false;
        if (blob) {
          navigator
            .share({
              text: `${this.shareText}
${location.protocol}//${location.host}${location.pathname}`,
              files: [new File([blob], "card.png", { type: "text/plain" })],
            })
            .then(() => {
              console.log("Share was successful.");
            })
            .catch((error) => {
              console.log("Sharing failed", error);
            });
        } else {
          console.error("failed to get blob.");
        }
      });
    } else {
      console.error("navigation.share not supported.");
    }
  }

  private copyToClipboard(text: string) {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        console.log("copied!");
        this.snackbar = true;
      })
      .catch((e) => {
        console.error(e);
      });
  }

  get ema(): boolean {
    return this.cardType ? this.cardType.bird_type == "ema" : false;
  }
}
